import { useCallback, useEffect, useMemo, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { isDesktop } from '@trello/browser';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { getFreeTrialProperties } from '@trello/business-logic/organization';
import { idToDate } from '@trello/dates';
import { Entitlements } from '@trello/entitlements';
import type { SentryErrorMetadata } from '@trello/error-reporting';
import { sendErrorEvent } from '@trello/error-reporting';
import {
  useFeatureGate,
  useGetExperimentValue,
} from '@trello/feature-gate-client';
import { intl } from '@trello/i18n';
import { showFlag } from '@trello/nachos/experimental-flags';
import { useLocation } from '@trello/router';
import { navigate } from '@trello/router/navigate';
import { getOrganizationHomeUrl } from '@trello/urls';

import { useStartFreeTrialForOrg } from 'app/src/components/FreeTrial';
import { useSelectNewUserOnboardingCampaign } from 'app/src/components/NewUserOnboardingCampaign/useSelectNewUserOnboardingCampaign';
import { PP_OPT_IN_MODAL_MESSAGE } from 'app/src/components/PersonalProductivityBeta/PPOptInModal/PPOptInModal';
import { useAssignPersonalProductivityCohort } from 'app/src/components/PersonalProductivityBeta/useAssignPersonalProductivityCohort';
import {
  splitScreenOnboardingCampaign,
  type SplitScreenOnboardingCampaign,
  type SplitScreenOnboardingStep,
} from 'app/src/components/SplitScreenOnboardingCampaign';
import { useMoonshotAddCampaignMutation } from './MoonshotAddCampaignMutation.generated';
import type { MoonshotCampaign, MoonshotStep } from './moonshotCampaign';
import { moonshotCampaign, MoonshotSteps } from './moonshotCampaign';
import { useMoonshotCreateFirstBoardQuery } from './MoonshotCreateFirstBoardQuery.generated';
import { useMoonshotOrganizationQuery } from './MoonshotOrganizationQuery.generated';
import type { MoonshotQuery } from './MoonshotQuery.generated';
import { useMoonshotQuery } from './MoonshotQuery.generated';
import moonshotQuery from './MoonshotQuery.graphql';
import { useMoonshotUpdateCampaignMutation } from './MoonshotUpdateCampaignMutation.generated';
import { validateReturnUrl } from './validateReturnUrl';

const _sendErrorEvent = (
  err: unknown,
  method: string,
  extraData: SentryErrorMetadata['extraData'],
) => {
  sendErrorEvent(err, {
    tags: {
      ownershipArea: 'trello-web-eng',
      feature: 'moonshot',
    },
    extraData: {
      component: 'MoonshotContext',
      // @ts-expect-error TS2339: Property 'networkError' does not exist on type '{}'.
      errorCode: err?.networkError?.extensions?.code ?? 'UNKNOWN_ERROR',
      method,
      ...extraData,
    },
  });
};

interface CurrentCampaign {
  currentCampaign?: NonNullable<MoonshotQuery['member']>['campaigns'][number];
  currentStepIsTerminal: boolean;
}
interface CurrentMoonshotCampaign extends MoonshotCampaign, CurrentCampaign {
  currentStep: MoonshotStep;
}

interface CurrentSplitScreenOnboardingCampaign
  extends SplitScreenOnboardingCampaign,
    CurrentCampaign {
  currentStep: SplitScreenOnboardingStep;
}

type CurrentCampaignContext =
  | CurrentMoonshotCampaign
  | CurrentSplitScreenOnboardingCampaign;

export const useMoonshotContext = () => {
  const [workspaceId, setWorkspaceId] = useState<string>('');
  const [hasCalledAddCampaign, setHasCalledAddCampaign] =
    useState<boolean>(false);
  const [campaign, setCampaign] = useState<
    NonNullable<MoonshotQuery['member']>['campaigns'][number] | null
  >(null);
  const [hasCreatedFirstWorkspace, setHasCreatedFirstWorkspace] =
    useState<boolean>(false);

  const { value: createCcpWorkspaceEnabled } = useFeatureGate(
    'billplat_ccp_create_workspace',
  );

  const { value: reverseTrialsEnabled } = useFeatureGate(
    'trello_reverse_trials',
  );

  const [startFreeTrialForOrg] = useStartFreeTrialForOrg();

  const {
    data: memberData,
    loading: loadingCampaign,
    // eslint-disable-next-line @trello/no-apollo-refetch
    refetch: refetchCampaignQuery,
  } = useMoonshotQuery({
    // eslint-disable-next-line @trello/disallow-fetch-policies
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    waitOn: ['MemberHeader', 'MemberBoards'],
  });
  const {
    data: workspaceData,
    // eslint-disable-next-line @trello/no-apollo-refetch
    refetch: refetchWorkspace,
    loading: isLoadingWorkspace,
  } = useMoonshotOrganizationQuery({
    variables: { orgId: workspaceId || '' },
    skip: !workspaceId,
    waitOn: ['MemberHeader', 'MemberBoards'],
  });
  const [
    addCampaign,
    { loading: isAddingCampaign, error: errorAddingCampaign },
  ] = useMoonshotAddCampaignMutation();
  const [updateCampaign, { loading: updatingCampaign }] =
    useMoonshotUpdateCampaignMutation();

  const loading = useMemo(
    () =>
      loadingCampaign ||
      isLoadingWorkspace ||
      isAddingCampaign ||
      updatingCampaign,
    [loadingCampaign, isLoadingWorkspace, isAddingCampaign, updatingCampaign],
  );

  const { member, campaigns, workspace, workspaces, enterprises, credits } =
    useMemo(() => {
      const _member = memberData?.member;
      const _workspace = workspaceData?.organization;

      return {
        member: _member,
        campaigns: _member?.campaigns ?? [],
        workspace: _workspace,
        workspaces: _member?.organizations ?? [],
        enterprises: _member?.enterprises ?? [],
        credits: _workspace?.credits ?? [],
      };
    }, [memberData?.member, workspaceData?.organization]);

  const currCampaign = useSelectNewUserOnboardingCampaign(
    member?.campaigns ?? [],
  );

  const {
    currentStep,
    currentStepIsTerminal,
    currentCampaign,
    initialStep,
    steps,
    orderedSteps,
  }: CurrentCampaignContext = useMemo(() => {
    const _currentStepIsTerminal = <A extends Array<T>, T>(
      terminalSteps: A,
      currStep: A[number],
    ) => {
      return terminalSteps.includes(currStep);
    };

    const isDefaultCampaign =
      !currCampaign?.name ||
      (currCampaign.name !== moonshotCampaign.name &&
        currCampaign.name !== splitScreenOnboardingCampaign.name);

    Analytics.sendOperationalEvent({
      action: 'selected',
      actionSubject: 'currentCampaign',
      source: 'moonshotContext',
      attributes: {
        campaign: isDefaultCampaign
          ? moonshotCampaign.name
          : currCampaign?.name,
        isDefaultCampaign,
      },
    });

    switch (currCampaign?.name) {
      case moonshotCampaign.name:
        return {
          currentCampaign: currCampaign,
          currentStep:
            (campaign?.currentStep as MoonshotStep) ||
            moonshotCampaign.initialStep,
          currentStepIsTerminal: _currentStepIsTerminal(
            moonshotCampaign.terminalSteps,
            (campaign?.currentStep as MoonshotStep) ||
              moonshotCampaign.initialStep,
          ),
          ...moonshotCampaign,
        };
      case splitScreenOnboardingCampaign.name:
        return {
          currentCampaign: currCampaign,
          currentStep:
            (campaign?.currentStep as SplitScreenOnboardingStep) ||
            splitScreenOnboardingCampaign.initialStep,
          currentStepIsTerminal: _currentStepIsTerminal(
            splitScreenOnboardingCampaign.terminalSteps,
            (campaign?.currentStep as SplitScreenOnboardingStep) ||
              splitScreenOnboardingCampaign.initialStep,
          ),
          ...splitScreenOnboardingCampaign,
        };
      default:
        // default to the moonshot campaign
        return {
          currentStepIsTerminal: false,
          currentStep: moonshotCampaign.initialStep,
          ...moonshotCampaign,
        };
    }
  }, [campaign?.currentStep, currCampaign]);

  const shadowItAuthorized = useMemo(
    () => member?.shadowItPolicy?.authorized,
    [member?.shadowItPolicy?.authorized],
  );

  const isFreeTrial = !!getFreeTrialProperties(
    credits,
    workspace?.offering || '',
    workspace?.paidAccount?.trialExpiration || '',
  )?.isActive;

  const needsToVerifyEmail = useMemo(
    () => workspace && reverseTrialsEnabled && !member?.confirmed,
    [member?.confirmed, reverseTrialsEnabled, workspace],
  );

  const idEnterprise =
    enterprises?.find(
      (ent) =>
        ent.id === member?.idEnterprise &&
        Entitlements.isEnterprise(ent.offering),
    )?.id || null;
  const isEnterprise = Boolean(idEnterprise);

  const { search } = useLocation();
  const returnUrl = new URLSearchParams(search).get('returnUrl');

  const { data: createFirstBoardData } = useMoonshotCreateFirstBoardQuery({
    variables: { memberId: member?.id || 'me' },
    skip:
      currentCampaign?.currentStep === initialStep ||
      !currentCampaign?.currentStep,
    waitOn: ['MemberHeader', 'MemberBoards'],
  });

  const createFirstBoardShortLink = useMemo(
    () => createFirstBoardData?.member?.boards?.[0]?.shortLink,
    [createFirstBoardData?.member?.boards],
  );

  const canApplyFreeTrial = useMemo(() => {
    if (!currentCampaign) {
      return false;
    }

    return (
      !isDesktop() &&
      workspace &&
      workspace.creationMethod !== 'proactive' &&
      reverseTrialsEnabled &&
      member?.confirmed &&
      !currentCampaign.dateDismissed
    );
  }, [currentCampaign, workspace, reverseTrialsEnabled, member?.confirmed]);

  /**
   * Soft-computes the next step in the wizard.
   *
   * Redirection is determined by the step that is passed
   * through the `onSubmit` / `onDismiss` hooks.
   */
  const getNextStep = useCallback(
    (
      key: MoonshotStep | SplitScreenOnboardingStep | null | undefined,
      isSkip: boolean,
    ) => {
      /**
       * we check key here to allow for skipping on the upsell screen
       */

      if (currentStepIsTerminal || isSkip) {
        return 'end';
      }

      if (key) {
        return key;
      }

      const _getNextStep = <T,>(ordered_steps: T[], curr_step: T) => {
        return ordered_steps.at(ordered_steps.indexOf(curr_step) + 1);
      };
      /**
       * Skip next step if it's verify-email and user is not in experiment
       */
      let nextStep = _getNextStep(orderedSteps, currentStep);
      if (nextStep === steps.VERIFY_EMAIL && !needsToVerifyEmail) {
        nextStep = _getNextStep(orderedSteps, nextStep);
      }
      return nextStep;
    },
    [
      currentStep,
      currentStepIsTerminal,
      needsToVerifyEmail,
      orderedSteps,
      steps.VERIFY_EMAIL,
    ],
  );

  const doRedirect = useCallback(
    (
      key: MoonshotStep | SplitScreenOnboardingStep | null | undefined,
      isSkip: boolean,
    ) => {
      const nextStep = getNextStep(key, isSkip);
      if (returnUrl && validateReturnUrl(returnUrl)) {
        window.location.assign(returnUrl);
        return;
      }
      // Sometimes the workspace does not exist, fallback to sending them to home in these cases
      if (!workspace?.name) {
        window.location.assign('/');
        return;
      }

      /**
       * if we set the nextStep to 'end' above because we're on welcome/ad/verify-email,
       * and didn't get redirected from the other conditions, send them to WS home
       */
      if (nextStep === 'end') {
        if (createFirstBoardShortLink) {
          navigate(`/b/${createFirstBoardShortLink}`, {
            trigger: true,
          });
        } else {
          window.location.assign(getOrganizationHomeUrl(workspace.name));
        }
      }
    },
    [getNextStep, returnUrl, workspace?.name, createFirstBoardShortLink],
  );

  const analyticsAttributes = useMemo(
    () => ({
      idEnterprise,
      isEnterprise,
      orgId: workspaceId,
      confirmed: member?.confirmed,
    }),
    [idEnterprise, isEnterprise, workspaceId, member?.confirmed],
  );

  /**
   * Old moonshot used one query for moonshot eligibility and campaign
   * state management (currentStep). When you created a workspace
   * on the first step, it sends a socket update that doesn't include
   * memberships, and as a result, it breaks the apollo cache and blocks
   * you from getting the correct currentStep from the useQuery hook.
   * So its now split to two queries.
   */
  const bestWorkspaceId =
    workspaces
      ?.filter((w) =>
        w?.memberships?.some(
          ({ idMember, memberType }) =>
            idMember === member?.id && memberType === 'admin',
        ),
      )
      .sort((a, b) => idToDate(b.id).getTime() - idToDate(a.id).getTime())[0]
      ?.id ?? '';

  const { campaign: experimentCampaignVariant } = useGetExperimentValue({
    experimentName: 'new_user_onboarding_flow_and_in-product_discovery',
    parameter: 'campaign',
    fireExposureEvent: false,
  });

  const { assignPersonalProductivityBetaCohort } =
    useAssignPersonalProductivityCohort();

  const { dismissOneTimeMessage } = useOneTimeMessagesDismissed();

  const addNewCampaign = useCallback(async () => {
    try {
      if (loadingCampaign) {
        return;
      }
      setHasCalledAddCampaign(true);

      // if the user is in the experimental treatment group, add the new user split screen onboarding campaign
      await addCampaign({
        variables: {
          name:
            experimentCampaignVariant === 'newUserSplitScreenOnboarding'
              ? splitScreenOnboardingCampaign.name
              : moonshotCampaign.name,
          currentStep:
            experimentCampaignVariant === 'newUserSplitScreenOnboarding'
              ? splitScreenOnboardingCampaign.initialStep
              : moonshotCampaign.initialStep,
        },
        /**
         * There is a strange bug where the component doesn't see the campaign
         * after adding it. This helps alleviate that issue by ensuring the cache
         * has the campaign
         */
        update: (cache, { data }) => {
          const campaignData = data?.addCampaign;
          if (!campaignData) {
            return;
          }

          cache.writeQuery({
            query: moonshotQuery,
            data: {
              member: {
                ...(member || {}),
                campaigns: campaigns.concat(campaignData),
              },
            },
          });
        },
      });
      if (experimentCampaignVariant !== 'not-enrolled') {
        await assignPersonalProductivityBetaCohort(
          experimentCampaignVariant === 'newUserSplitScreenOnboarding'
            ? 'ga'
            : 'new_user_holdout',
        );
      } else {
        // If the user is not-enrolled in the experiment, we want to ensure they never see the PP opt-in modal.
        dismissOneTimeMessage(PP_OPT_IN_MODAL_MESSAGE);
      }
    } catch (err) {
      _sendErrorEvent(err, 'addCampaign', { isEnterprise });
    }
  }, [
    loadingCampaign,
    addCampaign,
    experimentCampaignVariant,
    member,
    campaigns,
    assignPersonalProductivityBetaCohort,
    dismissOneTimeMessage,
    isEnterprise,
  ]);

  // used to end the campaign
  const onDismiss = useCallback(
    //We need to not redirect, in the case that we are clicking the Standard CTA on Upsell -
    //in that case we want to dismiss Moonshot but redirect to somewhere else.
    async (
      key: MoonshotStep | SplitScreenOnboardingStep | undefined = currentStep,
      { redirect } = { redirect: true },
    ) => {
      const source =
        currentStep === steps.VERIFY_EMAIL
          ? 'verifyEmailScreen'
          : 'moonshotCreateWorkspaceScreen';
      const traceId = Analytics.startTask({
        taskName: 'complete-new-user-onboarding',
        source,
      });
      try {
        if (!campaign) {
          throw new Error("Can't dismiss missing campaign");
        }

        await updateCampaign({
          variables: {
            id: campaign.id,
            currentStep,
            dateDismissed: new Date().toISOString(),
            isDismissed: true,
            traceId,
          },
        });
        Analytics.taskSucceeded({
          traceId,
          source,
          taskName: 'complete-new-user-onboarding',
        });
        if (redirect) {
          doRedirect(key, true);
        }
      } catch (err) {
        Analytics.taskFailed({
          traceId,
          source,
          taskName: 'complete-new-user-onboarding',
          error: err,
        });

        _sendErrorEvent(err, 'dismissCampaign', {
          currentStep:
            currentStep === initialStep ? null : (currentStep as string), // if we don't do this, we will send 'create-team' every time which is always inaccurate
          nextStep: null,
          credits: isFreeTrial,
          wasDismissed: true,
        });
        const errMsg = intl.formatMessage({
          id: 'templates.moonshot.error.step-transition-error',
          defaultMessage:
            'There was a problem continuing to the next step. Please refresh and try again.',
          description:
            'Error message indicating to user that a step transition problem occurred',
        });
        showFlag({
          appearance: 'error',
          title: errMsg,
          id: 'MoonshotContext',
          msTimeout: 5000,
        });
        throw new Error(errMsg);
      }
    },
    [
      currentStep,
      campaign,
      updateCampaign,
      doRedirect,
      initialStep,
      isFreeTrial,
      steps.VERIFY_EMAIL,
    ],
  );

  const onSubmit = useCallback(
    /**
     * The next step in the onboarding 'wizard' to transition to
     */
    async (
      key?: MoonshotStep | SplitScreenOnboardingStep,
      { redirect } = { redirect: true },
    ) => {
      const nextStep = getNextStep(key, false);

      if (!nextStep) {
        return;
      }

      try {
        // used to refetch the credits for the workspace if they start free trial
        if (currentStep === MoonshotSteps.PAYMENT_FORM) {
          await refetchWorkspace();
        }

        if (!campaign) {
          throw new Error("Can't update missing campaign");
        }

        if (!currentStepIsTerminal) {
          setCampaign({
            ...campaign,
            currentStep: nextStep,
          });
        }

        await updateCampaign({
          variables: {
            id: campaign.id,
            currentStep: currentStepIsTerminal ? currentStep : nextStep,
            // set dateDismissed if we are at the welcome or ad step
            ...(currentStepIsTerminal
              ? {
                  dateDismissed: new Date().toISOString(),
                  isDismissed: true,
                }
              : {
                  dateDismissed: null,
                }),
          },
        });

        Analytics.sendTrackEvent({
          action: 'advanced',
          actionSubject: 'step',
          actionSubjectId: 'moonshotStep',
          source:
            currentStep === steps.VERIFY_EMAIL
              ? 'verifyEmailScreen'
              : 'moonshotCreateWorkspaceScreen',
          attributes: {
            step: currentStep,
            ...analyticsAttributes,
          },
        });

        if (redirect && currentStepIsTerminal) {
          doRedirect(key, false);
        }
      } catch (err) {
        _sendErrorEvent(err, 'updateCampaign', {
          currentStep: currentStep as string,
          nextStep,
          credits: isFreeTrial,
        });
        const errMsg = intl.formatMessage({
          id: 'templates.moonshot.error.step-transition-error',
          defaultMessage:
            'There was a problem continuing to the next step. Please refresh and try again.',
          description:
            'Error message indicating to user that a step transition problem occurred',
        });
        showFlag({
          appearance: 'error',
          title: errMsg,
          id: 'MoonshotContext',
          msTimeout: 5000,
        });
        throw new Error(errMsg);
      }
    },
    [
      getNextStep,
      currentStep,
      campaign,
      currentStepIsTerminal,
      updateCampaign,
      steps.VERIFY_EMAIL,
      analyticsAttributes,
      refetchWorkspace,
      doRedirect,
      isFreeTrial,
    ],
  );

  /**
   * The user can only call `addCampaign` once.
   */
  const canCallAddCampaign = useMemo(() => {
    if (
      isAddingCampaign ||
      hasCalledAddCampaign ||
      errorAddingCampaign ||
      !member
    ) {
      return false;
    }

    return !currCampaign;
  }, [
    isAddingCampaign,
    hasCalledAddCampaign,
    errorAddingCampaign,
    member,
    currCampaign,
  ]);

  useEffect(() => {
    setWorkspaceId(bestWorkspaceId);
  }, [bestWorkspaceId]);

  useEffect(() => {
    if (loading || !currentCampaign) {
      return;
    }

    if (!campaign) {
      setCampaign(currentCampaign);
    }
  }, [loading, currentCampaign, campaign]);

  /**
   * If there's no workspace, make sure we set the step to 'create-team'.
   * This covers the case of users going through this flow more than once
   */
  useEffect(() => {
    if (isLoadingWorkspace || workspace) {
      return;
    }

    /**
     * We attempted to fetch a workspace but couldn't find one.
     * If there's no campaign -> no-op.
     * If there is a campaign, transition to first workspace creation step
     */
    if (currentStep !== initialStep && campaign) {
      setCampaign({
        ...campaign,
        currentStep: initialStep,
      });
    }
  }, [
    campaign,
    currentStep,
    initialStep,
    isLoadingWorkspace,
    workspace,
    needsToVerifyEmail,
  ]);

  /**
   * If it's the user's very first time, create their first campaign.
   */
  useEffect(() => {
    if (campaign) {
      return;
    }

    if (
      !loading &&
      !currentCampaign &&
      canCallAddCampaign &&
      !loadingCampaign
    ) {
      addNewCampaign();

      // Ensure unauthorized members are immediately redirected after the moonshot campaign is added
      if (!shadowItAuthorized && member?.shadowItPolicy?.redirectUrl) {
        window.location.assign(member.shadowItPolicy.redirectUrl);
        return;
      }
    } else if (hasCalledAddCampaign) {
      refetchCampaignQuery();
    }
  }, [
    addNewCampaign,
    loading,
    campaign,
    hasCalledAddCampaign,
    refetchCampaignQuery,
    canCallAddCampaign,
    loadingCampaign,
    shadowItAuthorized,
    member?.shadowItPolicy?.redirectUrl,
    currentCampaign,
  ]);

  /**
   * Use this effect to perform ad-hoc step updates, or to dismiss the campaign.
   */
  useEffect(() => {
    async function handle() {
      if (loading || !campaign) {
        return;
      }

      let nextStep: MoonshotStep | SplitScreenOnboardingStep | null = null;

      switch (currentStep) {
        case initialStep:
          /**
           * If a user is at the first step and they already have a workspace (which was not
           * proactively created), and they don't need to verify their account, we dismiss
           * the campaign for them.
           *
           * This logic will not be triggered for users who are only workspace guests.
           */
          if (
            !createCcpWorkspaceEnabled &&
            !hasCreatedFirstWorkspace &&
            workspaces.filter((wkspace) => {
              return wkspace.creationMethod !== 'proactive';
            }).length > 0
          ) {
            if (!needsToVerifyEmail) {
              return onDismiss();
            }
          }
          break;
        case steps.VERIFY_EMAIL:
          /**
           * Ensure that we always start the user's free trial if applicable
           */
          if (!needsToVerifyEmail) {
            if (canApplyFreeTrial) {
              await startFreeTrialForOrg(
                {
                  organization: workspace!,
                  count: 14,
                  via: 'reverse-trial',
                },
                'verifyEmailScreen',
              );
            }

            return onDismiss();
          }
          break;

        case MoonshotSteps.PAYMENT:
        case MoonshotSteps.PAYMENT_FORM:
        case MoonshotSteps.WELCOME:
          /**
           * Edge case of desktop user entering moonshot in web, then downloading desktop and
           * getting routed into the flow. In this case, we want to skip them to the success step.
           */
          if (isDesktop()) {
            nextStep = MoonshotSteps.WELCOME;
          }
          break;

        default:
          break;
      }

      if (nextStep) {
        try {
          await updateCampaign({
            variables: {
              id: campaign.id,
              currentStep: nextStep,
              dateDismissed: null,
            },
          });
        } catch (err) {
          _sendErrorEvent(err, 'updateCampaign', {
            currentStep: currentStep as string,
            nextStep,
            isEnterprise,
          });
        }
      }
    }
    handle();
  }, [
    updateCampaign,
    campaign,
    loading,
    currentStep,
    isEnterprise,
    needsToVerifyEmail,
    onDismiss,
    hasCreatedFirstWorkspace,
    createCcpWorkspaceEnabled,
    workspaces,
    startFreeTrialForOrg,
    canApplyFreeTrial,
    workspace,
    member,
    initialStep,
    steps.VERIFY_EMAIL,
  ]);

  const context = useMemo(
    () => ({
      currentStep,
      onSubmit,
      onDismiss,
      orgId: workspaceId,
      organization: workspace,
      loading,
      campaign: currentCampaign,
      isFreeTrial,
      member,
      idEnterprise,
      isEnterprise,
      setOrgId: setWorkspaceId,
      analyticsAttributes,
      setHasCreatedFirstWorkspace,
      shadowItAuthorized,
    }),
    [
      currentStep,
      onSubmit,
      onDismiss,
      workspaceId,
      workspace,
      loading,
      currentCampaign,
      isFreeTrial,
      member,
      idEnterprise,
      isEnterprise,
      setWorkspaceId,
      analyticsAttributes,
      shadowItAuthorized,
    ],
  );

  return context;
};
